import React, { useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  PopupChangeResult,
  PopupClosingTable,
  PopupVerification,
  PopupVerificationTypes,
  PopupChangeClassicBacResult,
} from 'molecules';
import { IPopupVerificationValues } from 'components/molecules/PopupVerification/PopupVerificationContainer';

import { tableClosingOptionsMock } from 'mocks/tableClosingOptionsMock';
import { ballOptions } from 'constants/common';
import { TablesClosingStatus } from 'constants/tables';
import { ICardsMap, TableStates } from 'types';
import { useModal, useOnRequestSuccess, useRequestError, useRequestLoading } from 'hooks';
import { getGameData } from 'core/selectors';
import {
  cancelAllCardsAction,
  cancelLastCardAction,
  cancelLastCardUsedAction,
  cancelRoundAction,
  changeCardsAction,
  changeCardsFailureAction,
  changeDealerAction,
  changeResultAction,
  changeResultFailureAction,
  changeResultVerifyAction,
  tableCloseAction,
  tableClosedAction,
  tableCloseVerifyAction,
} from 'data/reducers';
import { GameType } from 'types/tables';

export type TablePopupControls = {
  showChangeDealer: () => void;
  showCloseTableVerify: () => void;
  showCancelRound: () => void;
  showChangeResultVerify: () => void;
};

interface IProps {
  onMounted: (controls: TablePopupControls) => void;
  onPopupStateChange: (isAnyOpen: boolean) => void;
}

export const TablePagePopups = ({ onMounted, onPopupStateChange }: IProps) => {
  const dispatch = useDispatch();
  const {
    dealer,
    tableState,
    closed,
    changeResult,
    game_type,
    round: { cancelLastCardUsed, cards },
  } = useSelector(getGameData);

  const {
    isShown: isChangeDealerPopupShown,
    open: openChangeDealerPopup,
    close: closeChangeDealerPopup,
  } = useModal();
  const { isShown: isCancelRoundPopupShown, toggle: toggleCancelRoundPopup } = useModal();
  const { isShown: isChangeResultVerifyPopupShown, toggle: toggleChangeResultVerifyPopup } =
    useModal();
  const { isShown: isChangeResultPopupShown, toggle: toggleChangeResultPopup } = useModal();
  const { isShown: isCloseTableVerifyPopupShown, toggle: toggleCloseTableVerifyPopup } = useModal();
  const { isShown: isCloseTablePopupShown, toggle: toggleCloseTablePopup } = useModal();

  const { error: dealerChangedError, clearError: dealerChangedClearError } =
    useRequestError(changeDealerAction);
  const { error: cancelRoundError, clearError: cancelRoundClearError } =
    useRequestError(cancelRoundAction);
  const { error: changeResultVerifyError, clearError: changeResultVerifyClearError } =
    useRequestError(changeResultVerifyAction);
  const { error: changeResultError, clearError: changeResultClearError } =
    useRequestError(changeResultAction);
  const { error: closeTableError, clearError: closeTableClearError } =
    useRequestError(tableCloseVerifyAction);
  const { error: changeCardsError, clearError: changeCardsClearError } =
    useRequestError(changeCardsAction);
  const { error: cancelLastCardError, clearError: cancelLastCardClearError } =
    useRequestError(cancelLastCardAction);
  const { error: cancelAllCardsError, clearError: cancelAllCardsClearError } =
    useRequestError(cancelAllCardsAction);

  const changeDealerLoading = useRequestLoading(changeDealerAction);
  const cancelRoundLoading = useRequestLoading(cancelRoundAction);
  const closeTableVerifyLoading = useRequestLoading(tableCloseVerifyAction);
  const changeResultVerifyLoading = useRequestLoading(changeResultVerifyAction);
  const closeTableLoading = useRequestLoading(tableCloseAction);
  const changeResultLoading = useRequestLoading(changeResultAction);
  const changeCardsLoading = useRequestLoading(changeCardsAction);

  const isCancelLastCardDisabled = useMemo(
    () => cancelLastCardUsed || !cards || Object.keys(cards).length === 0,
    [cancelLastCardUsed, cards],
  );

  useEffect(() => {
    onMounted({
      showChangeDealer: openChangeDealerPopup,
      showCloseTableVerify: toggleCloseTableVerifyPopup,
      showCancelRound: toggleCancelRoundPopup,
      showChangeResultVerify: toggleChangeResultVerifyPopup,
    });
  }, [
    onMounted,
    openChangeDealerPopup,
    toggleCloseTableVerifyPopup,
    toggleCancelRoundPopup,
    toggleChangeResultVerifyPopup,
  ]);

  useEffect(() => {
    const isAnyPopupOpen =
      isChangeDealerPopupShown ||
      isCancelRoundPopupShown ||
      isChangeResultVerifyPopupShown ||
      isChangeResultPopupShown ||
      isCloseTableVerifyPopupShown ||
      isCloseTablePopupShown;

    onPopupStateChange(isAnyPopupOpen);
  }, [
    isChangeDealerPopupShown,
    isCancelRoundPopupShown,
    isChangeResultVerifyPopupShown,
    isChangeResultPopupShown,
    isCloseTableVerifyPopupShown,
    isCloseTablePopupShown,
    onPopupStateChange,
  ]);

  const onChangeDealer = useCallback(
    ({ id }: { id: string }) => {
      dispatch(changeDealerAction({ dealerId: id }));
    },
    [dispatch],
  );

  const onCancelRoundSubmit = useCallback(
    (values: IPopupVerificationValues) => {
      dispatch(cancelRoundAction({ shiftManagerId: values.id }));
    },
    [dispatch],
  );

  const onCloseTableVerifySubmit = useCallback(
    (values: { id: string }) => {
      dispatch(tableCloseVerifyAction({ shiftManagerId: values.id }));
    },
    [dispatch],
  );

  const onChangeResultVerifySubmit = useCallback(
    (values: { id: string }) => {
      dispatch(changeResultVerifyAction({ shiftManagerId: values.id }));
    },
    [dispatch],
  );

  const onCloseTableSubmit = useCallback(
    (value: TablesClosingStatus) => {
      if (closed?.verified && closed?.shiftManagerId) {
        dispatch(tableClosedAction());
        dispatch(tableCloseAction({ shiftManagerId: closed.shiftManagerId, closingStatus: value }));
      }
    },
    [dispatch, closed],
  );

  const onChangeResultSubmit = useCallback(
    (value: string) => {
      if (changeResult?.verified && changeResult?.shiftManagerId) {
        dispatch(
          changeResultAction({ shiftManagerId: changeResult.shiftManagerId, winner: value }),
        );
      }
      if (!changeResult?.verified || !changeResult?.shiftManagerId) {
        dispatch(changeResultFailureAction({ status: 601, message: 'NotAvailableAction' }));
      }
    },
    [dispatch, changeResult],
  );

  const onChangeCardsSubmit = useCallback(
    (value: ICardsMap) => {
      if (changeResult?.verified && changeResult?.shiftManagerId) {
        dispatch(changeCardsAction({ shiftManagerId: changeResult.shiftManagerId, cards: value }));
      }
      if (!changeResult?.verified || !changeResult?.shiftManagerId) {
        dispatch(changeCardsFailureAction({ status: 601, message: 'NotAvailableAction' }));
      }
    },
    [dispatch, changeResult],
  );

  const onCancelLastCard = useCallback(() => {
    if (!isCancelLastCardDisabled) {
      if (changeResult?.verified && changeResult?.shiftManagerId) {
        dispatch(cancelLastCardUsedAction(true));
        dispatch(cancelLastCardAction({ shiftManagerId: changeResult.shiftManagerId }));
        toggleChangeResultPopup();
      }
    }
  }, [dispatch, toggleChangeResultPopup, isCancelLastCardDisabled, changeResult]);

  const onReScanAllCards = useCallback(() => {
    if (changeResult?.verified && changeResult?.shiftManagerId) {
      dispatch(cancelAllCardsAction({ shiftManagerId: changeResult.shiftManagerId }));
      toggleChangeResultPopup();
    }
  }, [dispatch, toggleChangeResultPopup, changeResult]);

  useOnRequestSuccess(changeCardsAction, () => toggleChangeResultPopup());

  useEffect(() => {
    if (isChangeDealerPopupShown) {
      dealerChangedClearError();
    }
    if (isCancelRoundPopupShown) {
      cancelRoundClearError();
    }
    if (isCloseTableVerifyPopupShown) {
      closeTableClearError();
    }
    if (isChangeResultVerifyPopupShown) {
      changeResultVerifyClearError();
    }
    if (isChangeResultPopupShown) {
      changeResultClearError();
      changeCardsClearError();
      cancelLastCardClearError();
      cancelAllCardsClearError();
    }
  }, [
    isChangeDealerPopupShown,
    isCancelRoundPopupShown,
    isCloseTableVerifyPopupShown,
    isChangeResultVerifyPopupShown,
    isChangeResultPopupShown,
    cancelRoundClearError,
    closeTableClearError,
    changeResultClearError,
    changeResultVerifyClearError,
    dealerChangedClearError,
    changeCardsClearError,
    cancelLastCardClearError,
    cancelAllCardsClearError,
  ]);

  useEffect(() => {
    closeChangeDealerPopup();
  }, [dealer, closeChangeDealerPopup]);

  useEffect(() => {
    if (isCancelRoundPopupShown && tableState === TableStates.RoundCanceled) {
      toggleCancelRoundPopup();
    }
  }, [tableState, isCancelRoundPopupShown, toggleCancelRoundPopup]);

  useEffect(() => {
    if (changeResult?.verified) {
      toggleChangeResultVerifyPopup();
      toggleChangeResultPopup();
    }
  }, [changeResult?.verified, toggleChangeResultVerifyPopup, toggleChangeResultPopup]);

  useEffect(() => {
    if (closed?.verified) {
      toggleCloseTableVerifyPopup();
      toggleCloseTablePopup();
    }
  }, [closed?.verified, toggleCloseTableVerifyPopup, toggleCloseTablePopup]);

  useEffect(() => {
    if (closed?.verified && closed.isInProgress) {
      toggleCloseTablePopup();
    }
  }, [closed?.verified, closed?.isInProgress, toggleCloseTablePopup]);

  useEffect(() => {
    if (changeResult?.verified && changeResult.isInProgress && changeResult.isChanged) {
      toggleChangeResultPopup();
    }
  }, [
    changeResult?.verified,
    changeResult?.isInProgress,
    changeResult?.isChanged,
    toggleChangeResultPopup,
  ]);

  return (
    <>
      {isChangeDealerPopupShown
        ? createPortal(
            <PopupVerification
              title="Change dealer"
              type={PopupVerificationTypes.Dealer}
              error={dealerChangedError}
              loading={changeDealerLoading}
              clearError={dealerChangedClearError}
              onCancel={closeChangeDealerPopup}
              onSubmit={onChangeDealer}
            />,
            document.body,
          )
        : null}
      {isCancelRoundPopupShown
        ? createPortal(
            <PopupVerification
              title="Cancel round"
              type={PopupVerificationTypes.ShiftManager}
              error={cancelRoundError}
              loading={cancelRoundLoading}
              clearError={cancelRoundClearError}
              onCancel={toggleCancelRoundPopup}
              onSubmit={onCancelRoundSubmit}
            />,
            document.body,
          )
        : null}
      {isCloseTableVerifyPopupShown
        ? createPortal(
            <PopupVerification
              title="Close table"
              error={closeTableError}
              loading={closeTableVerifyLoading}
              type={PopupVerificationTypes.ShiftManager}
              clearError={closeTableClearError}
              onCancel={toggleCloseTableVerifyPopup}
              onSubmit={onCloseTableVerifySubmit}
            />,
            document.body,
          )
        : null}
      {isChangeResultVerifyPopupShown
        ? createPortal(
            <PopupVerification
              title="change result"
              error={changeResultVerifyError}
              loading={changeResultVerifyLoading}
              type={PopupVerificationTypes.ShiftManager}
              clearError={changeResultVerifyClearError}
              onCancel={toggleChangeResultVerifyPopup}
              onSubmit={onChangeResultVerifySubmit}
            />,
            document.body,
          )
        : null}
      {isCloseTablePopupShown
        ? createPortal(
            <PopupClosingTable
              options={tableClosingOptionsMock}
              loading={closeTableLoading}
              onClose={toggleCloseTablePopup}
              onSubmit={onCloseTableSubmit}
            />,
            document.body,
          )
        : null}
      {isChangeResultPopupShown
        ? game_type === GameType.BAC
          ? createPortal(
              <PopupChangeResult
                error={changeResultError}
                options={ballOptions}
                loading={changeResultLoading}
                onClose={toggleChangeResultPopup}
                onSubmit={onChangeResultSubmit}
              />,
              document.body,
            )
          : createPortal(
              <PopupChangeClassicBacResult
                onClose={toggleChangeResultPopup}
                manualInputLoading={changeCardsLoading}
                onSubmitManualInput={onChangeCardsSubmit}
                manualInputError={changeCardsError}
                onCancelLastCard={onCancelLastCard}
                cancelLastCardDisabled={isCancelLastCardDisabled}
                onReScanAllCards={onReScanAllCards}
                changeResultError={cancelLastCardError || cancelAllCardsError}
              />,
              document.body,
            )
        : null}
    </>
  );
};
